/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Contact/Support Page - Help page for user support
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Mail,
  MessageSquare,
  HelpCircle,
  FileQuestion,
  Shield,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  LogIn
} from 'lucide-react'
import { useBranding } from '../../stores/brandingStore'
import { useAuthStore } from '../../stores/authStore'
import feedbackService from '../../services/feedbackService'
import { getMetadata } from '../../utils/feedbackContext'
import { createLogger } from '../../utils/logger'

const logger = createLogger('Contact')

// Map contact categories to feedback categories
const categoryMapping = {
  general: 'other',
  technical: 'bug',
  account: 'other',
  billing: 'other',
  feedback: 'feature_request',
  privacy: 'other'
}

const Contact = () => {
  const branding = useBranding()
  const user = useAuthStore((state) => state.user)
  const [expandedFaq, setExpandedFaq] = useState(null)
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const faqs = [
    {
      question: 'How does the adaptive learning work?',
      answer:
        "Our AI analyzes your responses in real-time and adjusts question difficulty accordingly. If you answer questions correctly, the system will present harder questions. If you struggle, it will provide easier questions to help build your foundation. This keeps you in the optimal learning zone where you're challenged but not overwhelmed."
    },
    {
      question: 'What do the skill levels mean?',
      answer:
        "Each topic has 10 skill levels, from 1 (beginner) to 10 (advanced). As you consistently answer questions correctly at your current level, you'll level up to face harder questions. Higher levels correspond to more difficult SAT questions, so reaching level 7+ in a topic means you're ready for even the hardest SAT questions in that area."
    },
    {
      question: 'How often should I practice?',
      answer:
        'We recommend practicing for 20-30 minutes daily rather than longer sessions less frequently. Consistency is key for retention and skill building. Our research shows that students who practice regularly see significantly better improvement than those who cram.'
    },
    {
      question: 'Can my parent/guardian see my answers?',
      answer:
        'Guardians can see your overall progress, scores, and level advancement, but they cannot see your specific answers to questions. This protects your privacy while still allowing guardians to stay engaged with your SAT preparation journey.'
    },
    {
      question: 'How do I reset my password?',
      answer:
        'Click "Forgot Password" on the login page and enter your email address. You\'ll receive an email with a link to reset your password. The link expires after 1 hour for security reasons. If you don\'t receive the email, check your spam folder.'
    },
    {
      question: 'Is my data safe?',
      answer:
        'Yes, we take data security seriously. All data is encrypted in transit and at rest. We comply with privacy regulations including COPPA for users under 13 and CCPA for California residents. You can request your data or deletion at any time from your account settings.'
    },
    {
      question: 'What happens if I cancel a challenge midway?',
      answer:
        "If you exit a challenge before completing it, your progress is saved. The next time you log in, you'll be prompted to resume where you left off or start a new challenge. Incomplete challenges don't affect your skill levels."
    },
    {
      question: 'How accurate is the score prediction?',
      answer:
        'Our adaptive system provides skill level assessments that correlate with SAT performance. However, actual SAT scores depend on many factors including test-day conditions. Use our platform to build skills and track improvement rather than as an exact score predictor.'
    }
  ]

  const categories = [
    { value: 'general', label: 'General Question' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'account', label: 'Account Help' },
    { value: 'billing', label: 'Billing & Subscription' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'privacy', label: 'Privacy & Data' }
  ]

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!user) {
      setSubmitStatus('error')
      setErrorMessage('Please log in to submit a message.')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)
    setErrorMessage('')

    try {
      // Build the message with subject prefix and category context
      const categoryLabel =
        categories.find((c) => c.value === formData.category)?.label || formData.category
      const fullMessage = `[Contact Form - ${categoryLabel}]\nSubject: ${formData.subject}\n\n${formData.message}`

      // Create FormData for the feedback API
      const feedbackData = new FormData()
      feedbackData.append('category', categoryMapping[formData.category] || 'other')
      feedbackData.append('message', fullMessage)
      feedbackData.append('metadata', JSON.stringify(getMetadata()))

      await feedbackService.submitFeedback(feedbackData)

      setSubmitStatus('success')
      setFormData({
        subject: '',
        category: 'general',
        message: ''
      })
    } catch (error) {
      logger.error('Failed to submit contact form:', error)
      setSubmitStatus('error')
      setErrorMessage(error.response?.data?.message || 'Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <MessageSquare className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Contact & Support
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Get help with your account, report issues, or send us feedback
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Quick Help Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/help/getting-started"
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
          >
            <HelpCircle className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Getting Started</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              New to {branding.name}? Learn the basics
            </p>
          </Link>

          <Link
            to="/privacy-request"
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
          >
            <Shield className="w-8 h-8 text-green-600 dark:text-green-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Privacy Requests</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Data access, deletion, or corrections
            </p>
          </Link>

          <a
            href={`mailto:${branding.supportEmail}`}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow group"
          >
            <Mail className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Email Us</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{branding.supportEmail}</p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileQuestion className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="font-medium text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-600 dark:text-gray-400">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Send className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Send Us a Message</h2>
          </div>

          {/* Login prompt for non-authenticated users */}
          {!user && (
            <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
              <div className="flex items-start gap-3">
                <LogIn className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-primary-800 dark:text-primary-200">
                    Log in to send a message
                  </p>
                  <p className="text-sm text-primary-700 dark:text-primary-300 mb-3">
                    Please log in to submit a support request. Alternatively, email us directly.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      Log In
                    </Link>
                    <a
                      href={`mailto:${branding.supportEmail}`}
                      className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Email Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  Message sent successfully!
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  We'll get back to you within 24-48 hours. You can track your message in{' '}
                  <Link to="/my-feedback" className="underline hover:no-underline">
                    My Feedback
                  </Link>
                  .
                </p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">Failed to send message</p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {errorMessage ||
                    `Please try again or email us directly at ${branding.supportEmail}`}
                </p>
              </div>
            </div>
          )}

          {/* Show form only for authenticated users */}
          {user && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* User info display */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Submitting as{' '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {user.firstName} {user.lastName}
                  </span>{' '}
                  ({user.email})
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Brief description of your issue"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Please describe your question or issue in detail..."
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Typical response time: 24-48 hours
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Additional Resources */}
        <div className="bg-gray-100 dark:bg-gray-700/50 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Additional Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/legal/terms"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <ExternalLink className="w-4 h-4" />
              Terms of Service
            </Link>
            <Link
              to="/legal/privacy"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <ExternalLink className="w-4 h-4" />
              Privacy Policy
            </Link>
            <Link
              to="/privacy-request"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <ExternalLink className="w-4 h-4" />
              Submit Privacy Request
            </Link>
            <a
              href={`mailto:privacy@${branding.domain}`}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <ExternalLink className="w-4 h-4" />
              Privacy Inquiries
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
